// Lightweight volumetric clouds, shared by the sky dome (raymarch) and the terra ground (shadow
// projection). ONE coverage field + ONE set of params ⇒ the shadows on the ground always line up
// with the clouds overhead. The heavy work is a short raymarch inside the DOME fragment shader, but
// it only runs on SKY pixels: the dome is a true infinite skybox (depth pinned to the far plane,
// drawn last) so terrain always wins the depth test and the march never touches battlefield pixels.
//
// House-style "clever/cheap": a 2.5-D density model (2-D coverage fbm × a height profile), a faked
// single-tap sun-visibility term (Beer) + Henyey–Greenstein forward glow — no 3-D noise texture, no
// extra render pass. Ground shadows are FREE of a second march: we project the ground point up the
// sun ray to the slab and sample the very same coverage fbm.

import { styleOf } from "../terra/field";

export interface CloudParams {
  coverage: number; // 0 clear .. 1 solid overcast (fraction of sky filled)
  scale: number; // world studs per noise cell (bigger ⇒ larger cloud masses)
  y0: number; // slab bottom altitude (world Y)
  y1: number; // slab top altitude (world Y)
  density: number; // extinction multiplier (opacity of the body)
  windX: number; // drift velocity, studs/sec
  windZ: number;
  tint: [number, number, number]; // unlit body color (biome-tinted)
  shadowK: number; // ground-shadow strength 0..1 (0 ⇒ no ground shadows)
  steps: number; // primary raymarch steps (quality/cost knob)
}

// per-STYLE presets (0 garden / 1 rocky / 2 scorched / 3 ice — matches terra field.styleOf()).
// Slab altitudes sit ABOVE the highest survey camera (the framed spectator tops out ~6800 studs), so
// the deck always reads as "high overhead" from the ground and the framed view alike — never at eye level.
const PRESETS: CloudParams[] = [
  // 0 GARDEN — Earth-like fair-weather cumulus over the thick green-blue air
  { coverage: 0.44, scale: 3100, y0: 7500, y1: 10500, density: 1.9, windX: 39, windZ: 18, tint: [0.93, 0.96, 1.0], shadowK: 0.6, steps: 12 },
  // 1 ROCKY / temperate (Mars-rust) — thin, stretched dusty cirrus streaks, lighter shadows
  { coverage: 0.3, scale: 4600, y0: 8200, y1: 10000, density: 1.05, windX: 66, windZ: 12, tint: [0.87, 0.78, 0.64], shadowK: 0.34, steps: 10 },
  // 2 SCORCHED / hot (near-airless) — only sparse high ash veils; kept cheap (low coverage)
  { coverage: 0.13, scale: 5400, y0: 8600, y1: 10400, density: 0.9, windX: 30, windZ: 18, tint: [0.52, 0.36, 0.28], shadowK: 0.24, steps: 8 },
  // 3 ICE / cold — heavy pale overcast stratus, fast like the side-blown snow
  { coverage: 0.56, scale: 3500, y0: 7500, y1: 10000, density: 2.1, windX: 96, windZ: 33, tint: [0.88, 0.92, 0.98], shadowK: 0.55, steps: 12 },
];

export function cloudParamsForStyle(style: number): CloudParams {
  return PRESETS[style] ?? PRESETS[1];
}
export function cloudParamsForBiome(key: string): CloudParams {
  return cloudParamsForStyle(styleOf(key));
}
export function cloudSlabY(c: CloudParams): number {
  return (c.y0 + c.y1) * 0.5;
}

// The SHARED GLSL. Both the sky raymarch and the ground shadow #include this verbatim, so the two
// sample byte-identical coverage — shadows can never drift out of sync with the clouds that cast them.
export const CLOUD_COMMON = /* glsl */ `
  float clHash(vec2 p){ vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
  float clNoize(vec2 p){
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(clHash(i), clHash(i + vec2(1.0, 0.0)), u.x), mix(clHash(i + vec2(0.0, 1.0)), clHash(i + vec2(1.0, 1.0)), u.x), u.y);
  }
  float clFbm(vec2 p){
    float s = 0.0, a = 0.5;
    mat2 m = mat2(1.6, 1.2, -1.2, 1.6);
    for (int i = 0; i < 4; i++){ s += a * clNoize(p); p = m * p; a *= 0.5; }
    return s / 0.9375; // ⇒ 0..1
  }
  // fraction of sky filled at world-XZ position (0 clear .. 1 solid); windWorld drifts the field and t
  // slowly domain-warps it so the masses BILLOW and MORPH over time (not just slide as a rigid pattern).
  // The sky raymarch and the ground shadow #include this identically, so warped shadows track warped clouds.
  float cloudCover(vec2 xz, float coverage, float scale, vec2 windWorld, float t){
    vec2 p = (xz + windWorld) / scale;
    p += vec2(clNoize(p * 0.7 + t * 0.05), clNoize(p * 0.7 + 19.3 - t * 0.043)) * 0.32;
    float f = clFbm(p);
    return clamp((f - (1.0 - coverage)) / max(coverage, 0.001), 0.0, 1.0);
  }
`;
